import { ReactNode, useState } from "react";
import { Check, CaretDown, Gear, Lightbulb, BookOpen } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Dialog,
  DialogContent
} from "@libs/zapi-ui/shadcn";
import { InputLabel, Typography, AppButton, AppTextarea } from "@libs/zapi-ui/custom";
import { useProjectTemplates } from "@/zapi-frontend/widgets/CreateProjectForm/model/useProjectTemplates";
import { TProjectTemplate } from "@/zapi-frontend/widgets/CreateProjectForm";

interface SelectionDropdownProps {
  options: { value: string; label: string; component?: ReactNode }[];
  placeholder?: string;
  value: string | undefined;
  onChange: (value: string) => void;
  className?: string;
  error?: string;
  disabled?: boolean;
  label?: string;
  isOptional?: boolean;
  showSettings?: boolean;
  onSettingsClick?: () => void;
}

function SelectionDropdown({
  options,
  placeholder = "Select option...",
  value,
  onChange,
  className,
  error,
  disabled,
  label,
  isOptional,
  showSettings = false,
  onSettingsClick
}: SelectionDropdownProps) {
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className="flex flex-col relative">
      {label && (
        <InputLabel isOptional={isOptional} className="mb-[9px]">
          {label}
        </InputLabel>
      )}
      <div className="flex w-full border border-innoveraZinc-200 rounded-md">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild disabled={disabled}>
            <Button
              variant="ghost"
              role="combobox"
              aria-expanded={open}
              className={cn(
                "flex-1 justify-between font-normal border-0 rounded-r-none",
                value ? "text-zinc-900" : "text-innoveraZinc-400 hover:text-innoveraZinc-400",
                !!error && "border-innoveraRed-400",
                className
              )}
              size="default"
              disabled={disabled}
            >
              <span className="truncate">{selectedOption?.label || placeholder}</span>
              {!showSettings && <CaretDown className="opacity-50 flex-shrink-0 ml-2" />}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-auto min-w-[var(--radix-popover-trigger-width)] max-w-[400px] p-0"
            sideOffset={4}
          >
            <Command>
              <CommandList>
                <CommandEmpty>No option found.</CommandEmpty>
                <CommandGroup>
                  {options.map((option) => {
                    const isSelected = value === option.value;
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={(currentValue) => {
                          onChange(currentValue === value ? "" : currentValue);
                          setOpen(false);
                        }}
                        className={cn(
                          "min-w-0 hover:bg-[rgba(255,96,62,0.05)]",
                          isSelected && "rounded-md border border-[#FF603E] bg-[rgba(255,96,62,0.05)]"
                        )}
                      >
                        <span className="flex-1 whitespace-normal break-words">{option.component || option.label}</span>
                        <Check className={cn("ml-auto flex-shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {showSettings && value && (
          <button
            type="button"
            onClick={onSettingsClick}
            className="flex items-center gap-2 px-3 flex-shrink-0 border-l border-innoveraZinc-200 hover:bg-innoveraZinc-50 transition-colors"
          >
            <Gear className="w-4 h-4" />
            <span className="text-sm">Analysis Settings</span>
            <CaretDown className="w-4 h-4" />
          </button>
        )}
      </div>

      {error && <p className="absolute bottom-[-14px] left-1 text-innoveraRed-400 text-[10px]">{error}</p>}
    </div>
  );
}

interface InstructionsModalProps {
  open: boolean;
  onClose: () => void;
  template: TProjectTemplate | undefined;
  projectInstructions: string;
  chaptersInstructions: Record<string, string>;
  onSave: (projectInstructions: string, chaptersInstructions: Record<string, string>) => void;
}

function InstructionsModal({
  open,
  onClose,
  template,
  projectInstructions,
  chaptersInstructions,
  onSave
}: InstructionsModalProps) {
  const [selectedSection, setSelectedSection] = useState<string>("project-instructions");

  const isProjectInstructions = selectedSection === "project-instructions";
  const currentChapter = template?.projectTemplateChapters?.find((ch) => ch.slug === selectedSection);

  const handleInstructionChange = (newProjectInstructions: string, newChaptersInstructions: Record<string, string>) => {
    onSave(newProjectInstructions, newChaptersInstructions);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[1200px] h-[90vh] max-h-[750px] p-0 gap-0">
        <div className="flex h-full">
          <div className="w-[320px] bg-innoveraZinc-50 border-r border-innoveraZinc-200 flex flex-col rounded-l-lg">
            <div className="p-6 border-b border-innoveraZinc-200">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                <Typography variant="heading5">Instructions</Typography>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 max-h-[665px]">
              <div className="mb-6">
                <Typography variant="label4" color="zinc400" className="mb-3 px-3">
                  GENERAL INSTRUCTIONS
                </Typography>
                <button
                  onClick={() => setSelectedSection("project-instructions")}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors",
                    isProjectInstructions
                      ? "bg-white border border-innoveraZinc-200 shadow-sm"
                      : "hover:bg-innoveraZinc-100"
                  )}
                >
                  <Lightbulb className="w-5 h-5 flex-shrink-0" />
                  <Typography variant="body2" color={isProjectInstructions ? "zinc900" : "zinc700"}>
                    Project Instructions
                  </Typography>
                </button>
              </div>

              {template?.projectTemplateChapters && template.projectTemplateChapters.length > 0 && (
                <div>
                  <Typography variant="label4" color="zinc400" className="mb-3 px-3">
                    CHAPTERS INSTRUCTIONS
                  </Typography>
                  <div className="flex flex-col gap-1">
                    {template.projectTemplateChapters.map((chapter) => {
                      const isSelected = selectedSection === chapter.slug;
                      return (
                        <button
                          key={chapter.slug}
                          onClick={() => setSelectedSection(chapter.slug)}
                          className={cn(
                            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors",
                            isSelected
                              ? "bg-white border border-innoveraZinc-200 shadow-sm"
                              : "hover:bg-innoveraZinc-100"
                          )}
                        >
                          <BookOpen className="w-5 h-5 flex-shrink-0" />
                          <Typography variant="body2" color={isSelected ? "zinc900" : "zinc700"}>
                            {chapter.name}
                          </Typography>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-innoveraZinc-200">
              <Typography variant="heading5">
                {isProjectInstructions ? "Project Instructions" : currentChapter?.name || "Chapter Instructions"}
              </Typography>
            </div>

            <div className="flex-1 p-6 overflow-y-auto">
              <AppTextarea
                value={isProjectInstructions ? projectInstructions : chaptersInstructions[selectedSection] || ""}
                onChange={(e) => {
                  if (isProjectInstructions) {
                    handleInstructionChange(e.target.value, chaptersInstructions);
                  } else {
                    handleInstructionChange(projectInstructions, {
                      ...chaptersInstructions,
                      [selectedSection]: e.target.value
                    });
                  }
                }}
                placeholder={
                  isProjectInstructions
                    ? "Write Project Instructions"
                    : `Write instructions for ${currentChapter?.name || "this chapter"}`
                }
                className="min-h-[400px] w-full"
                fullWidth
              />
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-innoveraZinc-200">
              <AppButton appearance="contained" color="zinc950" onClick={onClose}>
                Save
              </AppButton>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface AnalysisDropdownProps {
  value: string | undefined;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  projectInstructions?: string;
  chaptersInstructions?: Record<string, string>;
  onInstructionsChange?: (projectInstructions: string, chaptersInstructions: Record<string, string>) => void;
}

export function AnalysisDropdown({
  value,
  onChange,
  error,
  disabled = false,
  projectInstructions = "",
  chaptersInstructions = {},
  onInstructionsChange
}: AnalysisDropdownProps) {
  const { projectTemplates, isProjectTemplatesLoading } = useProjectTemplates();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const selectedTemplate = projectTemplates.find((t) => t.slug === value);

  const options = projectTemplates.map((template) => ({
    value: template.slug,
    label: template.name,
    component: (
      <div className={cn("flex flex-col", template.description && "gap-2")}>
        <Typography variant="label2">{template.name}</Typography>
        {template.description && (
          <Typography variant="body3" color="zinc700">
            {template.description}
          </Typography>
        )}
      </div>
    )
  }));

  const handleSettingsClick = () => {
    setIsModalOpen(true);
  };

  const handleSaveInstructions = (newProjectInstructions: string, newChaptersInstructions: Record<string, string>) => {
    onInstructionsChange?.(newProjectInstructions, newChaptersInstructions);
  };

  return (
    <>
      <SelectionDropdown
        label="What type of analysis are you doing?*"
        disabled={disabled || isProjectTemplatesLoading}
        options={options}
        placeholder={isProjectTemplatesLoading ? "Loading..." : "Select"}
        value={value}
        onChange={onChange}
        error={error}
        showSettings={true}
        onSettingsClick={handleSettingsClick}
      />

      <InstructionsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        template={selectedTemplate}
        projectInstructions={projectInstructions}
        chaptersInstructions={chaptersInstructions}
        onSave={handleSaveInstructions}
      />
    </>
  );
}
